/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import type { CSSProperties } from 'react';

/**
 * Layout decisions for a listing page (properties, clients) wrapper stack.
 *
 * Shared so every listing screen lands on the same page padding, the same
 * gap under the control bar and the same scroll ownership: navigating from
 * Imóveis to Clientes must not shift the header by a pixel.
 *
 * The mobile agency layout (`AgencyDashboardLayout`) owns a single scroll
 * container (`flex-1 overflow-y-auto`). For the sticky control bar to pin to
 * that real scroller, the listing page wrappers must NOT introduce their own
 * `overflow: hidden` / height-capped scroll context on mobile — that would
 * trap the sticky element in a non-scrolling ancestor and detach it. On
 * desktop the nested overflow/min-height chain is preserved so the inner list
 * scrolls and the header stays fixed exactly as before.
 */
export interface ListingPageLayout {
  /** Outermost page wrapper inline style. */
  rootStyle: CSSProperties;
  /** Padded content wrapper inline style. */
  paddingStyle: CSSProperties;
  /** Vertical stack wrapper inline style. */
  stackStyle: CSSProperties;
  /** Class names for the infinite-scroll list container. */
  scrollContainerClassName: string;
}

/**
 * Computes the listing-page wrapper styles for the current breakpoint.
 *
 * @param {boolean} isMobile - Whether the mobile breakpoint is active.
 * @returns {ListingPageLayout} The wrapper styles and class names.
 */
export function getListingPageLayout(isMobile: boolean): ListingPageLayout {
  if (isMobile) {
    return {
      rootStyle: { width: '100%', background: 'var(--surface-secondary)', display: 'flex', flexDirection: 'column', fontFamily: 'var(--font-family)' },
      paddingStyle: { width: '100%', padding: 'var(--space-4) var(--space-3)', display: 'flex', flexDirection: 'column' },
      stackStyle: { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' },
      scrollContainerClassName: 'flex flex-col',
    };
  }

  return {
    rootStyle: { width: '100%', flex: 1, minHeight: 0, background: 'var(--surface-secondary)', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: 'var(--font-family)' },
    paddingStyle: { width: '100%', padding: 'var(--space-5) var(--space-6)', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' },
    stackStyle: { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', flex: 1, minHeight: 0, overflow: 'hidden' },
    scrollContainerClassName: 'flex flex-col flex-1 min-h-0 overflow-y-auto',
  };
}
